import { updateProfile } from 'firebase/auth';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { auth, db, storage } from './firebase';
import {
  AccountPreferences,
  AccountRole,
  EditableAccountProfile,
  EditableTeacherProfile,
  User,
} from '../types';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_PROFILE_IMAGE_BYTES = 5 * 1024 * 1024;

const notificationDefaults = {
  email: true,
  push: true,
  sessions: true,
  assignments: false,
  submissions: false,
  students: false,
  grades: false,
  payments: true,
  childActivity: false,
  attendance: false,
  announcements: true,
  relations: true,
  hr: true,
  security: true,
};

export const getDefaultAccountPreferences = (
  role: AccountRole
): AccountPreferences => ({
  language: 'ar',
  timezone: 'Africa/Cairo',
  appearance: {
    theme: 'system',
    fontSize: 'md',
    animations: true,
    reducedMotion: false,
    highContrast: false,
  },
  notifications: {
    ...notificationDefaults,
    assignments: role !== 'parent',
    submissions: role === 'teacher',
    students: role === 'teacher',
    grades: role !== 'teacher',
    childActivity: role === 'parent',
    attendance: role === 'parent',
  },
  privacy: {
    profileVisibility: role === 'teacher' ? 'public' : 'private',
    showContactInfo: false,
    allowMessages: true,
    activityStatus: true,
  },
});

const mergePreferences = (
  role: AccountRole,
  stored?: Partial<AccountPreferences>,
  legacy: Record<string, unknown> = {}
): AccountPreferences => {
  const defaults = getDefaultAccountPreferences(role);
  const legacyNotifications: Partial<AccountPreferences['notifications']> = {
    ...(typeof legacy.emailNotifications === 'boolean' && {
      email: legacy.emailNotifications,
    }),
    ...(typeof legacy.pushNotifications === 'boolean' && {
      push: legacy.pushNotifications,
    }),
  };

  return {
    ...defaults,
    ...stored,
    appearance: { ...defaults.appearance, ...stored?.appearance },
    notifications: {
      ...defaults.notifications,
      ...legacyNotifications,
      ...stored?.notifications,
    },
    privacy: { ...defaults.privacy, ...stored?.privacy },
  };
};

const normalizePreferencesForSave = (
  preferences: AccountPreferences
): AccountPreferences => ({
  language: preferences.language === 'en' ? 'en' : 'ar',
  timezone: preferences.timezone || 'Africa/Cairo',
  appearance: {
    theme: ['light', 'dark', 'system'].includes(preferences.appearance.theme)
      ? preferences.appearance.theme
      : 'system',
    fontSize: ['sm', 'md', 'lg', 'xl'].includes(preferences.appearance.fontSize)
      ? preferences.appearance.fontSize
      : 'md',
    animations: Boolean(preferences.appearance.animations),
    reducedMotion: Boolean(preferences.appearance.reducedMotion),
    highContrast: Boolean(preferences.appearance.highContrast),
  },
  notifications: {
    email: Boolean(preferences.notifications.email),
    push: Boolean(preferences.notifications.push),
    sessions: Boolean(preferences.notifications.sessions),
    assignments: Boolean(preferences.notifications.assignments),
    submissions: Boolean(preferences.notifications.submissions),
    students: Boolean(preferences.notifications.students),
    grades: Boolean(preferences.notifications.grades),
    payments: Boolean(preferences.notifications.payments),
    childActivity: Boolean(preferences.notifications.childActivity),
    attendance: Boolean(preferences.notifications.attendance),
    announcements: Boolean(preferences.notifications.announcements),
    relations: Boolean(preferences.notifications.relations),
    hr: Boolean(preferences.notifications.hr),
    security: Boolean(preferences.notifications.security),
  },
  privacy: {
    profileVisibility: ['public', 'private', 'students-only'].includes(
      preferences.privacy.profileVisibility
    )
      ? preferences.privacy.profileVisibility
      : 'private',
    showContactInfo: Boolean(preferences.privacy.showContactInfo),
    allowMessages: Boolean(preferences.privacy.allowMessages),
    activityStatus: Boolean(preferences.privacy.activityStatus),
  },
});

export interface AccountSettingsSnapshot {
  profile: EditableAccountProfile;
  teacherProfile: EditableTeacherProfile;
  preferences: AccountPreferences;
}

const mapSnapshot = (
  data: Record<string, any>,
  role: AccountRole
): AccountSettingsSnapshot => ({
  profile: {
    name: data.name || '',
    phone: data.phone || '',
    location: data.location || data.address || '',
    website: data.website || '',
    photoURL: data.photoURL || '',
  },
  teacherProfile: {
    bio: data.teacherProfile?.bio || '',
    subjects: Array.isArray(data.teacherProfile?.subjects)
      ? data.teacherProfile.subjects
      : [],
    experience: Number(data.teacherProfile?.experience || 0),
    qualifications: Array.isArray(data.teacherProfile?.qualifications)
      ? data.teacherProfile.qualifications
      : [],
  },
  preferences: mergePreferences(role, data.preferences, data),
});

export const accountSettingsService = {
  async get(userId: string, role: AccountRole): Promise<AccountSettingsSnapshot> {
    const snapshot = await getDoc(doc(db, 'users', userId));
    if (!snapshot.exists()) {
      throw new Error('تعذر العثور على بيانات الحساب.');
    }
    return mapSnapshot(snapshot.data(), role);
  },

  fromUser(user: User): AccountSettingsSnapshot {
    return mapSnapshot(user as unknown as Record<string, any>, user.role as AccountRole);
  },

  async saveProfile(
    userId: string,
    role: AccountRole,
    profile: EditableAccountProfile,
    teacherProfile?: EditableTeacherProfile
  ): Promise<void> {
    const userRef = doc(db, 'users', userId);
    const batch = writeBatch(db);
    const common = {
      name: profile.name.trim(),
      phone: profile.phone.trim(),
      location: profile.location.trim(),
      website: role === 'teacher' ? profile.website.trim() : '',
      ...(profile.photoURL !== undefined && { photoURL: profile.photoURL }),
      updatedAt: serverTimestamp(),
    };

    if (role === 'teacher' && teacherProfile) {
      const normalizedTeacher = {
        bio: teacherProfile.bio.trim(),
        subjects: teacherProfile.subjects.map(value => value.trim()).filter(Boolean),
        experience: Math.max(0, Number(teacherProfile.experience) || 0),
        qualifications: teacherProfile.qualifications
          .map(value => value.trim())
          .filter(Boolean),
      };
      batch.update(userRef, {
        ...common,
        'teacherProfile.bio': normalizedTeacher.bio,
        'teacherProfile.subjects': normalizedTeacher.subjects,
        'teacherProfile.experience': normalizedTeacher.experience,
        'teacherProfile.qualifications': normalizedTeacher.qualifications,
      });
      batch.set(
        doc(db, 'teachers', userId),
        {
          name: common.name,
          phone: common.phone,
          ...(profile.photoURL !== undefined && { photoURL: profile.photoURL }),
          bio: normalizedTeacher.bio,
          specialization: normalizedTeacher.subjects,
          experience: normalizedTeacher.experience,
          qualifications: normalizedTeacher.qualifications,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      batch.set(userRef, common, { merge: true });
    }

    await batch.commit();
    if (auth.currentUser?.uid === userId) {
      await updateProfile(auth.currentUser, {
        displayName: common.name,
        photoURL: profile.photoURL || auth.currentUser.photoURL,
      });
    }
  },

  async savePreferences(
    userId: string,
    preferences: AccountPreferences
  ): Promise<void> {
    await setDoc(
      doc(db, 'users', userId),
      { preferences: normalizePreferencesForSave(preferences), updatedAt: serverTimestamp() },
      { merge: true }
    );
  },

  validateImage(file: File): void {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('الصورة يجب أن تكون بصيغة JPG أو PNG أو WebP.');
    }
    if (file.size > MAX_PROFILE_IMAGE_BYTES) {
      throw new Error('حجم الصورة يجب ألا يتجاوز 5 ميجابايت.');
    }
  },

  async uploadProfileImage(
    userId: string,
    file: File
  ): Promise<string> {
    this.validateImage(file);
    const extension = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    const imageRef = ref(
      storage,
      `users/${userId}/profile/avatar-${Date.now()}.${extension}`
    );
    await uploadBytes(imageRef, file, {
      contentType: file.type,
      customMetadata: { ownerId: userId },
    });
    return getDownloadURL(imageRef);
  },

  async deleteProfileImage(photoURL?: string): Promise<void> {
    if (!photoURL || !photoURL.includes('/users%2F')) return;
    try {
      await deleteObject(ref(storage, photoURL));
    } catch (error) {
      console.warn('تعذر حذف صورة الملف السابقة.', error);
    }
  },
};

export default accountSettingsService;
